/**
 * ACTIVITY-CORE — pure feed-presentation helpers (UI-agnostic).
 * Human-readable text (i18n) lives in the app; this module only structures:
 * patch parsing, batch grouping, and detection of "offline edit".
 */

/** activity_recent row as it arrives from local SQLite. */
export interface ActivityRow {
  id: string;
  scope_id: string | null;
  branch_id: string | null;
  register_session_id: string | null;
  entity_type: string | null;
  entity_id: string | null;
  action: string | null;
  actor_user_id: string | null;
  actor_name: string | null;
  actor_device_id: string | null;
  platform: string | null;
  server_ts: string | null;
  client_ts: string | null;
  patch: string | null;
  batch_id: string | null;
  reverts_activity_id: string | null;
}

export type ActivityAction = "created" | "updated" | "deleted" | "restored" | "reverted";

export interface ActivityFieldChange {
  old: unknown;
  new: unknown;
}

export type ActivityPatch = Record<string, ActivityFieldChange>;

/** Type guard for a change `{old, new}` (no unsafe assertions). */
function isFieldChange(value: unknown): value is { old?: unknown; new?: unknown } {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

/** Parses the jsonb patch (arrives as TEXT in SQLite). Never throws. */
export function parseActivityPatch(raw: string | null | undefined): ActivityPatch {
  if (!raw) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    const patch: ActivityPatch = {};
    for (const [field, change] of Object.entries(parsed)) {
      if (isFieldChange(change)) {
        patch[field] = { old: change.old, new: change.new };
      }
    }
    return patch;
  } catch {
    return {};
  }
}

/** Changed fields of an entry, in stable order. */
export function changedFields(patch: ActivityPatch): string[] {
  return Object.keys(patch).toSorted();
}

/**
 * Threshold after which a client_ts↔server_ts difference is considered
 * "done offline and synced later" (rather than mere network latency).
 */
export const OFFLINE_LAG_THRESHOLD_MS = 60_000;

/**
 * Milliseconds between when the client made the change (client_ts)
 * and its arrival at the server (server_ts). `null` if either is missing or cannot be parsed.
 */
export function activityLagMs(row: Pick<ActivityRow, "server_ts" | "client_ts">): number | null {
  if (!row.server_ts || !row.client_ts) {
    return null;
  }
  const server = Date.parse(row.server_ts);
  const client = Date.parse(row.client_ts);
  if (Number.isNaN(server) || Number.isNaN(client)) {
    return null;
  }
  return server - client;
}

/** Was it done offline? (the client stamped it long before it reached the server) */
export function isOfflineEdit(
  row: Pick<ActivityRow, "server_ts" | "client_ts">,
  thresholdMs: number = OFFLINE_LAG_THRESHOLD_MS,
): boolean {
  const lag = activityLagMs(row);
  return lag !== null && lag > thresholdMs;
}

export interface ActivityBatchGroup<T extends ActivityRow = ActivityRow> {
  /** Shared batch_id (or the row id if it has no batch). */
  batchId: string;
  entries: T[];
}

/**
 * Groups CONSECUTIVE feed rows (already ordered by server_ts DESC) by batch_id:
 * "imported 40 products" is rendered as one group, not 40 cards. It does not reorder.
 */
export function groupActivityByBatch<T extends ActivityRow>(
  rows: readonly T[],
): ActivityBatchGroup<T>[] {
  const groups: ActivityBatchGroup<T>[] = [];
  for (const row of rows) {
    const key = row.batch_id ?? row.id;
    const last = groups.at(-1);
    if (last && last.batchId === key) {
      last.entries.push(row);
    } else {
      groups.push({ batchId: key, entries: [row] });
    }
  }
  return groups;
}

// ─── Shift summary (cash reconciliation) ──────────────────────────────────────

export interface ShiftActivitySummary {
  /** Number of voids (orders/lines marked void or soft-deleted). */
  voids: number;
  /** Number of discounts applied/changed. */
  discounts: number;
  /** Number of order reopens (paid/void → open). */
  reopens: number;
  /** Total number of shift entries. */
  total: number;
}

/** Does the entry represent a void? (order to void, or a voided line). */
export function isVoidEntry(row: ActivityRow): boolean {
  const patch = parseActivityPatch(row.patch);
  if (row.entity_type === "order" && patch.status?.new === "void") {
    return true;
  }
  return row.entity_type === "order_line" && row.action === "deleted";
}

/** Does the entry represent an applied or changed discount (value > 0)? */
export function isDiscountEntry(row: ActivityRow): boolean {
  if (row.entity_type !== "order") {
    return false;
  }
  const change = parseActivityPatch(row.patch).discount;
  return Boolean(change) && typeof change?.new === "number" && change.new > 0;
}

/** Does the entry represent a reopen (paid/void → open)? */
export function isReopenEntry(row: ActivityRow): boolean {
  if (row.entity_type !== "order") {
    return false;
  }
  const { status } = parseActivityPatch(row.patch);
  return Boolean(
    status && status.new === "open" && (status.old === "paid" || status.old === "void"),
  );
}

/**
 * Anti-fraud summary of a shift's activity (for cash reconciliation):
 * counts voids, discounts and reopens over the ALREADY filtered
 * rows by register_session_id.
 */
export function summarizeShiftActivity(rows: readonly ActivityRow[]): ShiftActivitySummary {
  let voids = 0;
  let discounts = 0;
  let reopens = 0;
  for (const row of rows) {
    if (isVoidEntry(row)) {
      voids += 1;
    }
    if (isDiscountEntry(row)) {
      discounts += 1;
    }
    if (isReopenEntry(row)) {
      reopens += 1;
    }
  }
  return { voids, discounts, reopens, total: rows.length };
}
